import React from "react";
import { RoomMessage } from "../../../entities/room-message.entity";
import { User } from "src/features/users/types/user.entity";
import { FiUser, FiMessageCircle } from "react-icons/fi";

type MessagesSectionProps = {
  messages: RoomMessage[];
  usersMap: Record<string, User>;
};

const formatDate = (date: Date | { toDate?: () => Date } | undefined): string => {
  if (!date) return "";
  
  // Handle Firestore Timestamp
  const actualDate = typeof (date as any).toDate === "function" 
    ? (date as any).toDate() 
    : new Date(date as any);
    
  return actualDate.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const MessagesSection: React.FC<MessagesSectionProps> = ({ messages, usersMap }) => {
  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FiMessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No messages in this room</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((message) => {
        const user = usersMap[message.userId];
        const userName = user?.name || message.userId;
        
        return (
          <div
            key={message.id}
            className="flex gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            {/* User Avatar */}
            <div className="flex-shrink-0">
              {user?.pictureURL ? (
                <img
                  src={user.pictureURL}
                  alt={userName}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <FiUser className="w-5 h-5 text-gray-500" />
                </div>
              )}
            </div>

            {/* Message Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-900 text-sm">
                  {userName}
                </span>
                <span className="text-xs text-gray-500">
                  {formatDate(message.createdAt)}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                {message.content}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
